import { useAppSelector } from '@/app/hooks';
import { monthIndex, monthKey } from '@/shared/lib';
import { useGetMetaQuery } from '../api/portalApi';
import type { PeriodQuery, PortalMeta, Scope } from '../api/types';
import { selectFilters } from './filtersSlice';
import { selectPrefs, type RevenueUnit } from './prefsSlice';

/** Global filters resolved against the data (port of the prototype's `periodOf`). */
export interface Period {
  meta: PortalMeta;
  /** Month indexes: from, to, length, previous-period end, current month, earliest month. */
  f: number;
  t: number;
  n: number;
  pt: number;
  current: number;
  earliest: number;
  /** MRR → ARR multiplier. */
  mul: number;
  unit: RevenueUnit;
  scope: Scope;
  /** Compare is on and a full previous period exists in the data. */
  compare: boolean;
  compareChosen: boolean;
  compareAvailable: boolean;
  query: PeriodQuery;
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));

/**
 * How often report pages re-check `meta` for a new backend snapshot (also on window focus and when
 * a page mounts with older meta). A new `generatedAt` invalidates the cached portal data.
 */
const META_REFRESH_SECONDS = 5 * 60;

export function usePeriod(): Period | undefined {
  const { data: meta } = useGetMetaQuery(undefined, {
    pollingInterval: META_REFRESH_SECONDS * 1000,
    refetchOnFocus: true,
    refetchOnMountOrArgChange: META_REFRESH_SECONDS,
  });
  const filters = useAppSelector(selectFilters);
  const prefs = useAppSelector(selectPrefs);
  if (!meta) return undefined;

  const current = monthIndex(meta.currentMonth);
  const earliest = monthIndex(meta.earliestMonth);
  const t = filters.to ? clamp(monthIndex(filters.to), earliest, current) : current;
  const f = filters.from
    ? clamp(monthIndex(filters.from), earliest, t)
    : clamp(t - prefs.range + 1, earliest, t);
  const n = t - f + 1;
  const compareAvailable = f - n >= earliest;
  const compare = filters.compare && compareAvailable;
  const scope: Scope = { offer: filters.offer, billing: filters.billing };
  return {
    meta,
    f,
    t,
    n,
    pt: f - 1,
    current,
    earliest,
    mul: filters.unit === 'ARR' ? 12 : 1,
    unit: filters.unit,
    scope,
    compare,
    compareChosen: filters.compare,
    compareAvailable,
    query: { from: monthKey(f), to: monthKey(t), ...scope, compare },
  };
}
